import { Avatar, Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, Field, Input, Label, OverlayDrawer, Spinner, Tag, TagPicker, TagPickerControl, TagPickerGroup, TagPickerInput, TagPickerList, TagPickerOption, TagPickerProps, Text, Textarea, Checkbox, MessageBar, MessageBarBody } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { connect } from "react-redux";
import { useEffect, useState, useCallback } from "react";
import { FontSizes, MessageBarType, Separator } from "@fluentui/react";

// component functions
import * as ComponentFunctions from './Payment.Form.Function';
import PaymentImputationsComponent from '../PaymentImputations/PaymentImputations.Component';

// components
import { CurrencySelectorComponent } from "../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent";
import { DateTimeInput } from "../../../../commons/component/Inputs/DateTimeInput/DateTimeInput";
import PersonSelectorComponent from "../../../../commons/component/Inputs/PersonSelector/PersonSelector.Component";
import ProtocolSelectorComponent from "../../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent";
import { MessageComponent } from "../../../../commons/component/MessageComponent";
import DebtSelectorComponent from "../../../../commons/component/DebtSelector/DebtSelector.Component";

// models
import { Person } from "../../../../models/Entities/Person/Person";
import { Protocol } from "../../../../models/Entities/Protocols/Protocol";
import { NewPayment } from "../../../../models/Entities/CashFlow/Payment/NewPayment";
import { Payment } from "../../../../models/Entities/CashFlow/Payment/Payment";
import { use } from "i18next";
import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";

// styles
import './Payment.Form.Styles.css';

interface FormValidation {
    success: boolean;
    errors: string[];
}

const PaymentFormComponent = (props: ComponentFunctions.Props) => {

    const styles = ComponentFunctions.useStyles();

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    const auxPerson: Person = {
        personIdentifier: props.OwnerId,
        id: 0,
        isDeleted: false,
        personType: 0,
        businessName: "Centro Médico"
    };

    // payment attributes
    const [currency, setCurrency] = useState<string | undefined>();
    const [paymentPayer, setPaymentPayer] = useState<Person | undefined>(props.DefaultPayer);
    const [paymentReceiver, setPaymentReceiver] = useState<Person | undefined>(props.DeFaultReceiver);
    const [paymentAmount, setPaymentAmount] = useState<number>(0);
    const [paymentAmountDisplay, setPaymentAmountDisplay] = useState<string>('');
    const [paymentDate, setPaymentDate] = useState<Date | undefined>(new Date());
    const [paymentComments, setPaymentComments] = useState<string>('');
    const [paymentProtocolId, setPaymentProtocolId] = useState<number | undefined>(undefined);
    const [invoiceAmount, setInvoiceAmount] = useState<number | undefined>(undefined);
    const [retentionAmount, setRetentionAmount] = useState<number | undefined>(undefined);
    const [bankChargesAmount, setBankChargesAmount] = useState<number | undefined>(undefined);
    const [imputesToDebt, setImputesToDebt] = useState<boolean>(false);
    const [debtsPaymentImputation, setDebtsPaymentImputation] = useState<DebtPaymentImputation[]>([]);

    // Memoized currency change handler to prevent infinite loops
    const handleCurrencyChange = useCallback((selectedCurrency: string | undefined) => {
        setCurrency(selectedCurrency);
    }, []);

    // if payment id is provided, get payment by id
    useEffect(() => {
        if (props.PaymentId && !props.CurrentPayment) {
            props.GetPaymentById(props.PaymentId);
        }
    }, [props.PaymentId]);

    // Usar identificadores primitivos como deps para evitar bucle por referencias inestables de objetos
    useEffect(() => {
        setPaymentDate(new Date());
    }, [props.DeFaultReceiver?.personIdentifier, props.DefaultPayer?.personIdentifier]);

    useEffect(() => {
        if (props.CurrentPayment) {
            setCurrency(props.CurrentPayment.currencyCode);
            setPaymentPayer(props.GetPersonByIdentifier(props.CurrentPayment.payer));
            setPaymentReceiver(props.GetPersonByIdentifier(props.CurrentPayment.receiver));
            setPaymentAmount(props.CurrentPayment.amount);
            setPaymentAmountDisplay(props.CurrentPayment.amount.toString());
            setPaymentDate(props.CurrentPayment.imputationDate);
            setPaymentComments(props.CurrentPayment.comments || '');
            setPaymentProtocolId(props.CurrentPayment.protocolId ?? undefined);
            setInvoiceAmount(props.CurrentPayment.invoiceAmount);
            setRetentionAmount(props.CurrentPayment.retentionAmount);
            setBankChargesAmount(props.CurrentPayment.bankChargesAmount);
        }
    // Solo re-inicializar cuando cambia el ID del pago, no en cada re-render por referencia
    }, [props.CurrentPayment?.id]);

    // Limpiar el formulario solo al desmontar el componente
    useEffect(() => {
        return () => {
            ResetForm();
        };
    }, []);

    useEffect(() => {
        if (props.isSaved && submittedForm) {
            ResetForm();
            props.onClose();
        }
    }, [props.isSaved]);


    useEffect(() => {
        if (props.failOnSaving) {
            setSubmittedForm(false);
        }
    }, [props.failOnSaving]);

    // form validation

    const ValidateForm = (): boolean => {

        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (!currency) {
            FormValidation.errors.push("Debe seleccionar una moneda");
            validationResult = false;
        }

        if (!paymentPayer) {
            FormValidation.errors.push("Debe seleccionar un pagador");
            validationResult = false;
        }

        if (!paymentReceiver) {
            FormValidation.errors.push("Debe seleccionar un cobrador");
            validationResult = false;
        }

        if (!paymentAmount) {
            FormValidation.errors.push("Debe ingresar un monto");
            validationResult = false;
        }

        if (!paymentDate) {
            FormValidation.errors.push("Debe ingresar una fecha de pago");
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };

    // reset form
    const ResetForm = () => {
        setSubmittedForm(false);
        setCurrency(undefined);
        setPaymentPayer(undefined);
        setPaymentReceiver(undefined);
        setPaymentAmount(0);
        setPaymentAmountDisplay('');
        setPaymentDate(undefined);
        setPaymentComments('');
        setPaymentProtocolId(undefined);
        setInvoiceAmount(undefined);
        setRetentionAmount(undefined);
        setBankChargesAmount(undefined);
        setImputesToDebt(false);
        setDebtsPaymentImputation([]);
    };

    //events

    const HandleCancelForm = () => {
        ResetForm();
        props.onClose();
    };
    const HandleSubmitForm = () => {

        setSubmittedForm(true);

        if (ValidateForm()) {
            // submit form
            if (!props.CurrentPayment) {
                const newPayment: NewPayment = {
                    payer: paymentPayer!.personIdentifier!,
                    receiver: paymentReceiver!.personIdentifier!,
                    currencyCode: currency!,
                    amount: paymentAmount!,
                    invoiceAmount: invoiceAmount,
                    retentionAmount: retentionAmount,
                    bankChargesAmount: bankChargesAmount,
                    imputationDate: paymentDate!,
                    paymentDate: paymentDate!,
                    comments: paymentComments,
                    debtsPaymentImputation: debtsPaymentImputation,
                    protocolId: paymentProtocolId ?? null
                };
                props.CreatePayment(newPayment)
            } else {
                const payment: Payment = {
                    id: props.CurrentPayment.id,
                    payer: paymentPayer!.personIdentifier!,
                    receiver: paymentReceiver!.personIdentifier!,
                    currencyCode: currency!,
                    amount: paymentAmount!,
                    invoiceAmount: invoiceAmount,
                    retentionAmount: retentionAmount,
                    bankChargesAmount: bankChargesAmount,
                    imputationDate: paymentDate!,
                    paymentDate: paymentDate!,
                    comments: paymentComments,
                    debtsPaymentImputation: debtsPaymentImputation,
                    protocolId: paymentProtocolId ?? null
                };
                props.UpdatePayment(payment);
            }
        } else {
            setSubmittedForm(false);
        }
    };

    const PayerPicker = (
        <PersonSelectorComponent
            personSelected={props.CurrentPayment ? [props.CurrentPayment?.payer] : props.DefaultPayer ? [props.DefaultPayer.personIdentifier] : []}
            selectionLimit={1}
            isRequired={true}
            fieldLabel={"Pagador"}
            CustomAditionalPersonsList={[{
                id: 0,
                businessName: 'Centro Médico',
                personIdentifier: props.OwnerId,
                personType: 0,
                isDeleted: false
            } as Person]}
            onChangeSelection={(selectedPersons: Person[]) => setPaymentPayer(selectedPersons[0])}
        />
    );

    const ReceiverPicker = (
        <PersonSelectorComponent
            personSelected={props.CurrentPayment ? [props.CurrentPayment?.receiver] : props.DeFaultReceiver ? [props.DeFaultReceiver.personIdentifier] : []}
            selectionLimit={1}
            isRequired={true}
            fieldLabel={"Receptor"}
            CustomAditionalPersonsList={[{
                id: 0,
                businessName: 'Centro Médico',
                personIdentifier: props.OwnerId,
                personType: 0,
                isDeleted: false
            } as Person]}
            onChangeSelection={(selectedPersons: Person[]) => setPaymentReceiver(selectedPersons[0])}
        />
    );

    const FormFields = (
        <div className={styles.content}>

            {/* ── Sección 1: Partes involucradas ── */}
            <div className={styles.sectionCard}>
                <Text className={styles.sectionTitle}>Partes involucradas</Text>
                {props.FormType === ComponentFunctions.FormType.payer
                    ? <>{PayerPicker}{ReceiverPicker}</>
                    : <>{ReceiverPicker}{PayerPicker}</>
                }
            </div>

            {/* ── Sección 2: Detalle del pago ── */}
            <div className={styles.sectionCard}>
                <Text className={styles.sectionTitle}>Detalle del pago</Text>
                <div className={styles.row}>
                    <div className={styles.currencyCol}>
                        <CurrencySelectorComponent
                            errorMessage={submittedForm && !currency ? "Seleccione una moneda" : undefined}
                            defaultSelected={props.CurrentPayment?.currencyCode || undefined}
                            label="Moneda"
                            isRequired={true}
                            onCurrencyChange={handleCurrencyChange}
                        />
                    </div>
                    <div className={styles.amountCol}>
                        <Field
                            label="Monto"
                            required
                            validationState={submittedForm && paymentAmount <= 0 ? "error" : "none"}
                            validationMessage={submittedForm && paymentAmount <= 0 ? "Ingrese un monto mayor a 0" : undefined}
                        >
                            <Input
                                key={`payment-amount-${props.CurrentPayment?.id ?? 'new'}`}
                                placeholder="0.00"
                                type="text"
                                inputMode="decimal"
                                defaultValue={paymentAmountDisplay}
                                contentAfter={currency ? <Text>{currency}</Text> : undefined}
                                onChange={(e, data) => {
                                    // Input NO controlado (defaultValue + key): el DOM maneja el texto,
                                    // así las re-renderizaciones del DebtSelector/Redux no interfieren con el tipeo.
                                    const raw = data.value.replace(/[^0-9.]/g, '');
                                    const numeric = parseFloat(raw);
                                    setPaymentAmount(isNaN(numeric) ? 0 : numeric);
                                    setPaymentAmountDisplay(raw);
                                }}
                            />
                        </Field>
                        {paymentAmount > 0 && (
                            <Text className={styles.amountHint}>
                                {paymentAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                            </Text>
                        )}
                    </div>
                </div>
                <DateTimeInput
                    label={"Fecha de pago"}
                    placeHolder={"Fecha de pago"}
                    selectedDate={paymentDate}
                    onSelectDate={(date: Date | undefined) => setPaymentDate(date)}
                    isSubmitted={submittedForm}
                    isRequired={true}
                    errorMessage={undefined}
                />
            </div>

            {/* ── Sección 3: Información adicional ── */}
            <div className={styles.sectionCard}>
                <Text className={styles.sectionTitle}>Información adicional</Text>
                <Field label="Protocolo (opcional)">
                    <ProtocolSelectorComponent
                        selectionLimit={1}
                        selectedProtocols={paymentProtocolId === undefined ? [] : [{ id: paymentProtocolId } as Protocol]}
                        onChangeSelection={(protocolsSelected: Protocol[] | undefined) => {
                            const v = (protocolsSelected || [])[0];
                            setPaymentProtocolId(v?.id);
                        }}
                    />
                </Field>
                <Field label="Comentarios">
                    <Textarea
                        value={paymentComments || ''}
                        onChange={(e) => setPaymentComments(e.currentTarget.value)}
                    />
                </Field>
            </div>

            {/* ── Sección 4: Imputación a deudas ── */}
            <div className={styles.sectionCard}>
                <Text className={styles.sectionTitle}>Imputación a deudas</Text>
                <Checkbox
                    checked={imputesToDebt}
                    onChange={(e, data) => setImputesToDebt(data.checked === true)}
                    label="Este pago imputa a deudas existentes"
                />
                {imputesToDebt && paymentPayer && paymentReceiver && currency && (
                    <DebtSelectorComponent
                        debtor={paymentPayer}
                        creditor={paymentReceiver}
                        paymentCurrency={currency}
                        paymentAmount={paymentAmount}
                        onImputationsChange={(imputations) => setDebtsPaymentImputation(imputations)}
                        selectedImputations={debtsPaymentImputation}
                    />
                )}
                {imputesToDebt && !(paymentPayer && paymentReceiver && currency) && (
                    <MessageBar intent="info">
                        <MessageBarBody>
                            Para imputar a deudas primero debe completar:
                            {!paymentPayer && <> pagador,</>}
                            {!paymentReceiver && <> receptor,</>}
                            {!currency && <> moneda</>}
                            .
                        </MessageBarBody>
                    </MessageBar>
                )}
            </div>
        </div>
    );

    // Calculate unimputed amount
    // Note: debtsPaymentImputation amounts are in debt currency, need to convert to payment currency
    const totalImputed = debtsPaymentImputation.reduce((sum, imp) => {
        // Convert from debt currency to payment currency: amountInPaymentCurrency = amountInDebtCurrency * exchangeRate
        const amountInPaymentCurrency = imp.amount * imp.exchangeRate;
        return sum + amountInPaymentCurrency;
    }, 0);
    const unimputedAmount = paymentAmount - totalImputed;
    const hasUnimputedAmount = imputesToDebt && debtsPaymentImputation.length > 0 && unimputedAmount > 0.01;

    // Footer como JSX directo — definir un componente dentro del render causa unmount/remount en cada render
    const FooterJsx = (
        <DrawerFooter>
            <div className={styles.footerActions}>
                <Button onClick={HandleCancelForm} disabled={submittedForm}>Cancelar</Button>
                <Button appearance="primary" onClick={HandleSubmitForm} disabled={submittedForm}>{props.isSaving && <><Spinner size="extra-tiny" />&nbsp;</>}Guardar</Button>
            </div>
        </DrawerFooter>
    );

    return (
        <div>
            <OverlayDrawer
                position={'end'}
                open={props.isOpen}
                size="large"
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={() => HandleCancelForm()}
                            />
                        }
                    >
                        Información de pago
                    </DrawerHeaderTitle>
                    {FormValidationStatus?.success === false && <MessageComponent message={"Verifique la información suministrada"} type={MessageBarType.error} itemList={FormValidationStatus?.errors} />}
                    {props.failOnSaving === true && <MessageComponent message={props.error?.ErrorMessage || 'Ocurrio un error inesperado'} type={MessageBarType.error} itemList={props.error?.Errors} />}
                    {hasUnimputedAmount && (

                        <div>
                            <Separator />
                            <MessageBar intent="warning">
                                <MessageBarBody>
                                    El monto a imputar ({totalImputed.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}) no alcanza el total del pago ({paymentAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}). El monto excedente de {unimputedAmount.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency} quedará sin imputar y podrá imputarse posteriormente.
                                </MessageBarBody>
                            </MessageBar>

                        </div>
                    )}
                </DrawerHeader>

                <DrawerBody className='ScrollShadow'>
                    {/* Siempre renderizar FormFields — nunca desmontarlo por CurrentPayment.
                        Desmontarlo causaba que al remontar se reinicializara el estado local desde
                        props.CurrentPayment, pisando los valores que el usuario había ingresado. */}
                    {props.PaymentId && !props.CurrentPayment
                        ? <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}><Spinner label="Cargando pago..." /></div>
                        : FormFields
                    }
                    {props.PaymentId && props.CurrentPayment && (
                        <>
                            <Separator />
                            <PaymentImputationsComponent
                                paymentId={props.PaymentId}
                            />
                        </>
                    )}
                </DrawerBody>
                {FooterJsx}
            </OverlayDrawer>
        </div>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PaymentFormComponent as any);